/**
 * The ops SPA's one way to read data (ops audit 2026-10-02, Phase 2 bet 1).
 *
 * Design: knowledge/ops-portal/specs/2026-10-03-data-loading-primitive-design.md
 *
 * Screens call `useOpsQuery`, never `@tanstack/vue-query` directly: the
 * wrapper is the exit path if the library is ever swapped, and the place the
 * audit's rules live:
 *
 *  - Four exclusive states. `empty` is only reachable after a successful read,
 *    so a failed read can never render as "nothing to review".
 *  - The cache key always carries the signed-in user, and every input the
 *    read depends on is in the key. A reply for client A is filed under A's
 *    key; it cannot land on client B's screen. No `placeholderData` /
 *    `keepPreviousData`: showing the previous selection's rows while the next
 *    loads is the exact bug class this replaces.
 *  - A change of signed-in user clears the whole cache (registerIdentityReset).
 *  - Superseded requests are aborted: `fetch` receives the query's AbortSignal.
 *  - Polling (`refetchInterval`) pauses in hidden tabs (the library's
 *    `refetchIntervalInBackground` defaults to false).
 */
import { computed, reactive, toValue, type App, type MaybeRefOrGetter } from 'vue'
import axios from 'axios'
import { QueryClient, VueQueryPlugin, useQuery, useQueryClient } from '@tanstack/vue-query'
import { getApiErrorMessage, registerIdentityReset, useAuthStore } from '@claimshield/shared'

/** One retry for a dropped connection or a 5xx; a 4xx is the server's answer, never retried. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false
  if (axios.isAxiosError(error)) {
    if (axios.isCancel(error)) return false
    const status = error.response?.status
    return status === undefined || status >= 500
  }
  return false
}

export function createOpsQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // A screen asks for a refresh (refetch, invalidate, interval) explicitly;
        // a tab switch does not silently reload what someone is reading.
        refetchOnWindowFocus: false,
      },
    },
  })
}

/** Install once in main.ts. Returns the client so tests and mutations can reach it. */
export function installOpsQuery(app: App, client: QueryClient = createOpsQueryClient()): QueryClient {
  app.use(VueQueryPlugin, { queryClient: client })
  // Signing in as someone else (or out) drops every cached read of the last user.
  registerIdentityReset(() => client.clear())
  return client
}

export type OpsQueryStatus = 'idle' | 'loading' | 'error' | 'empty' | 'data'

export interface OpsQueryOptions<T> {
  /** Every input the read depends on (ids, folder, filters). The user id is added here. */
  key: MaybeRefOrGetter<readonly unknown[]>
  /** The read. Pass `signal` to axios (`{ signal }`) so a superseded request is aborted. */
  fetch: (signal: AbortSignal) => Promise<T>
  /** False while a required input is missing (no client selected): the state is `idle`. */
  enabled?: MaybeRefOrGetter<boolean>
  /** When a successful answer means "nothing here". Without it, a success is always `data`. */
  isEmpty?: (data: T) => boolean
  /** Poll every N ms while the tab is visible. */
  refetchInterval?: number
  /** How long an answer counts as fresh, in ms (default 0: re-read on every mount). */
  staleTime?: number
}

export function useOpsQuery<T>(options: OpsQueryOptions<T>) {
  const auth = useAuthStore()
  const client = useQueryClient()
  const queryKey = computed(() => ['ops', auth.user?.id ?? null, ...toValue(options.key)])
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => options.fetch(signal),
    enabled: computed(() => (options.enabled === undefined ? true : toValue(options.enabled))),
    refetchInterval: options.refetchInterval,
    staleTime: options.staleTime ?? 0,
  })

  const status = computed<OpsQueryStatus>(() => {
    const enabled = options.enabled === undefined ? true : toValue(options.enabled)
    // Data in hand stays on screen; a failed refresh is reported beside it (refreshError).
    if (query.data.value !== undefined) {
      return options.isEmpty?.(query.data.value as T) ? 'empty' : 'data'
    }
    if (query.isError.value) return 'error'
    if (!enabled) return 'idle'
    return 'loading'
  })

  return reactive({
    status,
    data: computed(() => query.data.value as T | undefined),
    /** Why the read failed, in words for the screen; null unless status is `error`. */
    error: computed(() => (status.value === 'error' ? getApiErrorMessage(query.error.value) : null)),
    /** The thrown error itself, for a screen that branches on it (an HTTP status). */
    cause: computed(() => (query.isError.value ? query.error.value : null)),
    /** A refresh failed while the last good answer is still shown. */
    refreshError: computed(() =>
      query.isError.value && query.data.value !== undefined ? getApiErrorMessage(query.error.value) : null,
    ),
    /** True during any read, first or refresh. */
    fetching: computed(() => query.isFetching.value),
    retry: () => query.refetch(),
    /**
     * Change the answer on screen after a write the server has confirmed
     * (drop a routed row). Applies to the current key only, and does nothing
     * before the first answer.
     */
    setData: (change: (current: T) => T) =>
      void client.setQueryData<T>(queryKey.value, (current) => (current === undefined ? current : change(current))),
  })
}

export type OpsQuery<T> = ReturnType<typeof useOpsQuery<T>>

/**
 * Re-read every query whose key starts with `prefix` (the same key a screen
 * passed to useOpsQuery, without the user id), after a write that changed it:
 * `invalidate(['driver-files-client-detail', clientId])`. Call in setup.
 */
export function useOpsQueryInvalidate() {
  const auth = useAuthStore()
  const client = useQueryClient()
  return (prefix: readonly unknown[]) =>
    client.invalidateQueries({ queryKey: ['ops', auth.user?.id ?? null, ...prefix] })
}
